/**
 * Singleton admin pages for SmartImprove — Executive Report Template.
 *
 * Note: `EngagementDefaultsPage` was moved to its own dedicated module at
 * `/app/frontend/src/admin/smartimprove/EngagementDefaultsPage.jsx`
 * (Feb 2026 cleanup) — the engagement-defaults singleton API + storage
 * layer is unchanged. This file now ships only the Executive Report
 * Template singleton, which still mirrors SmartEvaluate's `BrandingPage`
 * pattern.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Save } from "lucide-react";
import { reportTemplateApi } from "../../lib/smartimproveAdminApi";

function Card({ title, description, children, testId }) {
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm p-5 space-y-4"
      data-testid={testId}
    >
      <header>
        <h2 className="text-sm font-semibold text-[#111827]">{title}</h2>
        {description ? (
          <p className="text-xs text-[#6B7280] mt-1">{description}</p>
        ) : null}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function LabeledInput({ label, ...props }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#374151] mb-1">{label}</label>
      <Input className="rounded-sm text-xs" {...props} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Executive Report Template                                          */
/* ------------------------------------------------------------------ */
export function ReportTemplatePage() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    reportTemplateApi.get().then(setDoc).catch(() => setDoc({}));
  }, []);

  if (!doc) return <div className="text-sm text-[#6B7280]">Loading…</div>;

  const set = (k, v) => setDoc((s) => ({ ...s, [k]: v }));
  const setBranding = (k, v) =>
    setDoc((s) => ({ ...s, branding: { ...(s.branding || {}), [k]: v } }));
  const setNarrative = (k, v) =>
    setDoc((s) => ({
      ...s,
      narrative_placeholders: { ...(s.narrative_placeholders || {}), [k]: v },
    }));

  const save = async () => {
    setSaving(true);
    try {
      const next = await reportTemplateApi.put(doc);
      setDoc(next);
      toast.success("Report template saved");
    } catch {
      toast.error("Failed to save report template");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="si-report-template">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Client Output
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Executive Report Template
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Default branding, section ordering, and narrative placeholders for the
            SmartImprove executive summary. The renderer ships in Phase 3.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="si-report-template-save"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </header>

      <Card title="Branding">
        <div className="grid grid-cols-2 gap-4">
          <LabeledInput
            label="Primary color"
            value={doc.branding?.primary_color || ""}
            onChange={(e) => setBranding("primary_color", e.target.value)}
          />
          <LabeledInput
            label="Accent color"
            value={doc.branding?.accent_color || ""}
            onChange={(e) => setBranding("accent_color", e.target.value)}
          />
          <label className="text-xs text-[#374151] flex items-center gap-2 col-span-2">
            <input
              type="checkbox"
              checked={doc.branding?.show_partner_logo !== false}
              onChange={(e) => setBranding("show_partner_logo", e.target.checked)}
            />
            Show partner logo on cover page
          </label>
        </div>
      </Card>

      <Card title="Sections & ordering">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Sections (comma-separated slugs, in order)
          </label>
          <Input
            className="rounded-sm text-xs"
            value={(doc.sections || []).join(", ")}
            onChange={(e) =>
              set(
                "sections",
                e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
              )
            }
            data-testid="si-report-template-sections"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Recommendation ordering
          </label>
          <Select
            value={doc.recommendation_ordering || "by_business_impact"}
            onValueChange={(v) => set("recommendation_ordering", v)}
          >
            <SelectTrigger className="rounded-sm text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[
                { value: "by_business_impact", label: "By business impact" },
                { value: "by_severity", label: "By severity" },
                { value: "by_operational_area", label: "By operational area" },
              ].map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card title="Narrative placeholders">
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Intro
          </label>
          <Textarea
            className="rounded-sm text-xs"
            rows={3}
            value={doc.narrative_placeholders?.intro || ""}
            onChange={(e) => setNarrative("intro", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Closing
          </label>
          <Textarea
            className="rounded-sm text-xs"
            rows={3}
            value={doc.narrative_placeholders?.closing || ""}
            onChange={(e) => setNarrative("closing", e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#374151] mb-1">
            Disclaimer text
          </label>
          <Textarea
            className="rounded-sm text-xs"
            rows={2}
            value={doc.disclaimer_text || ""}
            onChange={(e) => set("disclaimer_text", e.target.value)}
          />
        </div>
      </Card>
    </div>
  );
}
